import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Package, Calendar, Search, Plus, Trash2, ClipboardList, Truck, CheckCircle2, FileEdit, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  abierto: { label: 'Abierto', cls: 'bg-slate-100 text-slate-700' },
  en_camino: { label: 'En Camino', cls: 'bg-blue-100 text-blue-700' },
  recibido: { label: 'Recibido', cls: 'bg-emerald-100 text-emerald-700' },
};

function OrdersBlock({ title, icon: Icon, orders, loading, branchName, emptyText, testId }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-6" data-testid={testId}>
      <div className="flex items-center justify-between bg-slate-50 border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#1D3C6E]" />
          <h2 className="text-sm font-semibold text-[#1D3C6E] font-['Outfit'] uppercase tracking-wide">{title}</h2>
        </div>
        <span className="text-xs font-medium text-slate-500 bg-white px-2 py-0.5 rounded-full border border-slate-200">{orders.length}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-slate-100">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-slate-500 text-xs">Folio</th>
              <th className="text-left px-4 py-2 font-medium text-slate-500 text-xs">Fecha</th>
              <th className="text-left px-4 py-2 font-medium text-slate-500 text-xs">Sucursal</th>
              <th className="text-center px-4 py-2 font-medium text-slate-500 text-xs">Artículos</th>
              <th className="text-center px-4 py-2 font-medium text-slate-500 text-xs">Piezas</th>
              <th className="text-right px-4 py-2 font-medium text-slate-500 text-xs">Costo total</th>
              <th className="text-left px-4 py-2 font-medium text-slate-500 text-xs">Creado por</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-6 text-slate-400 text-xs">Cargando…</td></tr>}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-slate-400 text-xs">{emptyText}</td></tr>
            )}
            {orders.map(o => (
              <tr key={o.id} className="border-b border-slate-50 hover:bg-slate-50 last:border-b-0">
                <td className="px-4 py-2.5">
                  <Link to={`/purchase-orders/${o.id}`} className="font-mono text-xs font-medium text-[#1D3C6E] hover:underline" data-testid={`order-link-${o.id}`}>
                    {o.order_number}
                  </Link>
                </td>
                <td className="px-4 py-2.5 text-slate-600 text-xs">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3 h-3 text-slate-400" />
                    {new Date(o.created_at).toLocaleString('es-MX', { timeZone: 'America/Hermosillo' })}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-slate-700 text-xs">{branchName(o.branch_id)}</td>
                <td className="px-4 py-2.5 text-center font-medium">{o.total_items}</td>
                <td className="px-4 py-2.5 text-center font-medium">{o.total_qty}</td>
                <td className="px-4 py-2.5 text-right font-medium text-[#1D3C6E]">${(o.total_cost || 0).toFixed(2)}</td>
                <td className="px-4 py-2.5 text-slate-600 text-xs">{o.created_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ArticlesByOrderBlock({ branches, currentBranch, onOrderCreated, getAuthHeaders }) {
  const [cart, setCart] = useState([]);
  const [cartLoading, setCartLoading] = useState(true);
  const [selected, setSelected] = useState({}); // { line_id: bool }
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ name: '', modelo: '', color: '', cantidad: '1' });
  const [targetBranch, setTargetBranch] = useState(currentBranch || (branches[0]?.id || ''));
  const [saving, setSaving] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!currentBranch && branches[0]?.id) setTargetBranch(branches[0].id);
    else if (currentBranch) setTargetBranch(currentBranch);
  }, [currentBranch, branches]);

  // --- Load cart from DB ---
  const fetchCart = async () => {
    setCartLoading(true);
    try {
      const r = await axios.get(`${API}/api/purchase-orders/draft`, { headers: getAuthHeaders() });
      setCart(r.data || []);
    } catch (err) { console.error(err); }
    finally { setCartLoading(false); }
  };
  useEffect(() => { fetchCart(); }, []);

  // Debounced search
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(`${API}/api/inventory?search=${encodeURIComponent(q)}`, { headers: getAuthHeaders() });
        setSuggestions((res.data || []).slice(0, 12));
        setSearchOpen(true);
      } catch (err) { console.error(err); }
      finally { setSearchLoading(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [search, getAuthHeaders]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const branchName = (id) => branches.find(b => b.id === id)?.name || '—';

  const addInventoryItem = async (invItem) => {
    try {
      await axios.post(`${API}/api/purchase-orders/draft`, {
        type: 'inv',
        item_id: invItem.id,
        branch_id: targetBranch,
        cantidad: 1,
      }, { headers: getAuthHeaders() });
      await fetchCart();
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo agregar');
    }
    setSearch('');
    setSuggestions([]);
    setSearchOpen(false);
  };

  const addManualItem = async (e) => {
    e.preventDefault();
    const name = manualForm.name.trim();
    if (!name) return;
    const qty = parseInt(manualForm.cantidad) || 1;
    try {
      await axios.post(`${API}/api/purchase-orders/draft`, {
        type: 'custom',
        name,
        modelo: manualForm.modelo.trim(),
        color: manualForm.color.trim(),
        branch_id: targetBranch,
        cantidad: qty,
      }, { headers: getAuthHeaders() });
      await fetchCart();
      setManualForm({ name: '', modelo: '', color: '', cantidad: '1' });
      setManualOpen(false);
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo agregar');
    }
  };

  const updateQty = async (line, val) => {
    const qty = parseInt(val) || 0;
    if (qty <= 0) return;
    // optimistic UI
    setCart(prev => prev.map(c => c.id === line.id ? { ...c, cantidad: qty } : c));
    try {
      await axios.patch(`${API}/api/purchase-orders/draft/${line.id}`,
        { cantidad: qty }, { headers: getAuthHeaders() });
    } catch (err) { fetchCart(); }
  };

  const updateBranch = async (line, branchId) => {
    setCart(prev => prev.map(c => c.id === line.id ? { ...c, branch_id: branchId } : c));
    try {
      await axios.patch(`${API}/api/purchase-orders/draft/${line.id}`,
        { branch_id: branchId }, { headers: getAuthHeaders() });
    } catch (err) { fetchCart(); }
  };

  const removeItem = async (line) => {
    setCart(prev => prev.filter(c => c.id !== line.id));
    try {
      await axios.delete(`${API}/api/purchase-orders/draft/${line.id}`, { headers: getAuthHeaders() });
    } catch (err) { fetchCart(); }
  };

  const clearCart = async () => {
    if (!window.confirm('¿Vaciar la lista de artículos por pedir?')) return;
    try {
      await axios.delete(`${API}/api/purchase-orders/draft`, { headers: getAuthHeaders() });
      setCart([]);
      setSelected({});
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo vaciar');
    }
  };

  const toggleSelected = (lineId) => setSelected(prev => ({ ...prev, [lineId]: !prev[lineId] }));
  const toggleAll = () => {
    const allOn = cart.length > 0 && cart.every(l => selected[l.id]);
    if (allOn) setSelected({});
    else {
      const next = {};
      cart.forEach(l => { next[l.id] = true; });
      setSelected(next);
    }
  };

  const generate = async () => {
    const selectedLines = cart.filter(c => selected[c.id] && (c.cantidad || 0) > 0);
    if (selectedLines.length === 0) { alert('Selecciona al menos un artículo con cantidad mayor a 0'); return; }
    setSaving(true);
    try {
      const items = selectedLines.map(c => c.type === 'inv'
        ? { item_id: c.item_id, branch_id: c.branch_id || targetBranch, cantidad_pedida: c.cantidad, modelo: c.modelo, color: c.color, provider: c.provider }
        : { item_id: '', name: c.name, modelo: c.modelo, color: c.color, provider: c.provider, branch_id: c.branch_id || targetBranch, cantidad_pedida: c.cantidad }
      );
      await axios.post(`${API}/api/purchase-orders`, {
        branch_id: targetBranch,
        items,
      }, { headers: getAuthHeaders() });
      // Remove ONLY the selected lines from the draft cart
      for (const l of selectedLines) {
        try { await axios.delete(`${API}/api/purchase-orders/draft/${l.id}`, { headers: getAuthHeaders() }); } catch (e) { /* ignore */ }
      }
      setSelected({});
      await fetchCart();
      if (onOrderCreated) onOrderCreated();
    } catch (err) {
      alert(err.response?.data?.detail || 'No se pudo generar el pedido');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-6" data-testid="articles-to-order-block">
      <div className="flex items-center justify-between bg-slate-50 border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-[#AFBD20]" />
          <h2 className="text-sm font-semibold text-[#1D3C6E] font-['Outfit'] uppercase tracking-wide">Artículos por Pedir</h2>
        </div>
        <span className="text-xs font-medium text-slate-500 bg-white px-2 py-0.5 rounded-full border border-slate-200">{cart.length}</span>
      </div>

      <div className="p-4 space-y-3">
        {/* Search + Manual actions */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
          <div className="relative" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              className="pl-10"
              placeholder="Buscar artículo en inventario (SKU, nombre, modelo)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => search.trim().length >= 2 && setSearchOpen(true)}
              data-testid="cart-search-input"
            />
            {searchOpen && (suggestions.length > 0 || searchLoading) && (
              <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid="cart-search-dropdown">
                {searchLoading && <div className="px-3 py-2 text-xs text-slate-400">Buscando…</div>}
                {suggestions.map(it => (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => addInventoryItem(it)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-50 last:border-b-0"
                    data-testid={`cart-suggestion-${it.id}`}
                  >
                    <span className="font-mono text-xs text-slate-500 w-16 shrink-0">{it.sku}</span>
                    <span className="flex-1 text-sm font-medium text-slate-800 truncate">{it.name}</span>
                    <Plus className="w-4 h-4 text-[#AFBD20] shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button
            type="button"
            onClick={() => setManualOpen(v => !v)}
            variant="outline"
            className="whitespace-nowrap"
            data-testid="cart-manual-toggle"
          >
            <FileEdit className="w-4 h-4 mr-2" />
            {manualOpen ? 'Cancelar' : 'Agregar manual'}
          </Button>
        </div>

        {/* Manual form */}
        {manualOpen && (
          <form onSubmit={addManualItem} className="border border-slate-200 rounded-lg p-3 bg-slate-50/60" data-testid="cart-manual-form">
            <p className="text-xs font-medium text-slate-600 mb-2">Agregar artículo manual (no está en inventario)</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <div>
                <label className="text-[11px] text-slate-500">Nombre *</label>
                <Input value={manualForm.name} onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })} required data-testid="manual-name" />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Modelo</label>
                <Input value={manualForm.modelo} onChange={(e) => setManualForm({ ...manualForm, modelo: e.target.value })} data-testid="manual-modelo" />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Color</label>
                <Input value={manualForm.color} onChange={(e) => setManualForm({ ...manualForm, color: e.target.value })} data-testid="manual-color" />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Cantidad *</label>
                <Input type="number" min="1" value={manualForm.cantidad} onChange={(e) => setManualForm({ ...manualForm, cantidad: e.target.value })} required data-testid="manual-cantidad" />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button type="submit" className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="manual-add-btn">
                <Plus className="w-4 h-4 mr-2" /> Agregar a la lista
              </Button>
            </div>
          </form>
        )}

        {/* Cart list */}
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    className="rounded"
                    checked={cart.length > 0 && cart.every(l => selected[l.id])}
                    onChange={toggleAll}
                    data-testid="cart-select-all"
                  />
                </th>
                <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Tipo</th>
                <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>SKU</th>
                <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Modelo</th>
                <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider">Nombre</th>
                <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Color</th>
                <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Sucursal</th>
                <th className="text-center px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Cant.</th>
                <th className="text-left px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}>Proveedor</th>
                <th className="text-right px-3 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '1%', whiteSpace: 'nowrap' }}></th>
              </tr>
            </thead>
            <tbody>
              {cartLoading && (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-slate-400 text-xs">Cargando…</td>
                </tr>
              )}
              {!cartLoading && cart.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-slate-400 text-xs">
                    <Package className="w-6 h-6 mx-auto mb-1 opacity-40" />
                    Aún no has agregado artículos. Usa el buscador o el botón &quot;Agregar manual&quot;.
                  </td>
                </tr>
              )}
              {!cartLoading && cart.map(line => (
                <tr key={line.id} className="border-b border-slate-100 last:border-b-0" data-testid={`cart-line-${line.id}`}>
                  <td className="px-3 py-2" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={!!selected[line.id]}
                      onChange={() => toggleSelected(line.id)}
                      data-testid={`cart-check-${line.id}`}
                    />
                  </td>
                  <td className="px-3 py-2" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${line.type === 'inv' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                      {line.type === 'inv' ? 'Inventario' : 'Manual'}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500" style={{ width: '1%', whiteSpace: 'nowrap' }}>{line.sku || '—'}</td>
                  <td className="px-3 py-2 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{line.modelo || '—'}</td>
                  <td className="px-3 py-2 font-medium text-slate-800">
                    {line.name}
                    {line.repair_ticket && (
                      <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-50 text-purple-700" title="Solicitada desde reparación">
                        {line.repair_ticket}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{line.color || '—'}</td>
                  <td className="px-3 py-2" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                    <Select value={line.branch_id || ''} onValueChange={(v) => updateBranch(line, v)}>
                      <SelectTrigger className="h-8 w-32 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-center" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                    <input
                      type="number"
                      min="1"
                      value={line.cantidad}
                      onChange={(e) => updateQty(line, e.target.value)}
                      className="w-20 h-8 rounded border border-slate-200 text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#AFBD20]/40 focus:border-[#AFBD20]"
                      data-testid={`cart-qty-${line.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600" style={{ width: '1%', whiteSpace: 'nowrap' }}>{line.provider || '—'}</td>
                  <td className="px-3 py-2 text-right" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      onClick={() => removeItem(line)}
                      className="text-red-400 hover:text-red-600"
                      title="Eliminar"
                      data-testid={`cart-remove-${line.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer: generate button only */}
        {cart.length > 0 && (
          <div className="flex justify-end pt-2">
            <Button
              type="button"
              onClick={generate}
              disabled={saving || !cart.some(l => selected[l.id])}
              className="bg-[#AFBD20] hover:bg-[#9aa61c] text-[#1D3C6E] font-semibold disabled:opacity-50"
              data-testid="cart-generate-btn"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              {saving ? 'Generando...' : 'Generar Pedido'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PurchaseOrders() {
  const { getAuthHeaders, currentBranch, branches } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchOrders(); }, [currentBranch]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentBranch) params.append('branch', currentBranch);
      const res = await axios.get(`${API}/api/purchase-orders?${params}`, { headers: getAuthHeaders() });
      setOrders(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const branchName = (id) => branches.find(b => b.id === id)?.name || '—';

  const grouped = useMemo(() => {
    const g = { abierto: [], en_camino: [], recibido: [] };
    for (const o of orders) {
      const s = o.status === 'draft' ? 'abierto' : o.status;
      if (g[s]) g[s].push(o);
    }
    return g;
  }, [orders]);

  return (
    <div data-testid="purchase-orders-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">Pedidos</h1>
      </div>

      <ArticlesByOrderBlock
        branches={branches}
        currentBranch={currentBranch}
        getAuthHeaders={getAuthHeaders}
        onOrderCreated={fetchOrders}
      />

      <OrdersBlock
        title="Pedidos Abiertos"
        icon={FileEdit}
        orders={grouped.abierto}
        loading={loading}
        branchName={branchName}
        emptyText="No hay pedidos abiertos"
        testId="orders-block-abierto"
      />

      <OrdersBlock
        title="Pedidos en Camino"
        icon={Truck}
        orders={grouped.en_camino}
        loading={loading}
        branchName={branchName}
        emptyText="No hay pedidos en camino"
        testId="orders-block-en-camino"
      />

      <OrdersBlock
        title="Pedidos Recibidos"
        icon={CheckCircle2}
        orders={grouped.recibido}
        loading={loading}
        branchName={branchName}
        emptyText="No hay pedidos recibidos"
        testId="orders-block-recibido"
      />
    </div>
  );
}
